// ── Notifications Modal (C1351, row-click/redesign TPT22, hover-expand rows TPT9188) ──
//
// Shows the caller's ENTIRE cross-project notification list — deliberately unfiltered by
// their digest_mode/transactional_mode settings (Settings ▸ Notifications, C1350): those
// only gate what gets emailed, never what this list shows (see tt-api-notifications.md).
// Data comes from GET /api/workspace/notifications (lib/notifications.js's
// fetchNotifications()), which also returns the caller's workspace rows — project_id NULL,
// the one daily digest covering all of their projects (TPT9186). Mark-read goes through the
// same module's markNotificationReadOnce(), which republishes the shared unread count to every
// subscriber (the navbar bell badge updates without a re-render).
//
// Rows (TPT9188): each row is clamped to its title plus a one-line preview. Hovering a row
// (after a short dwell) or giving it keyboard focus expands it in place: its slot keeps its
// height and its card becomes an overlay over the rows below (or above, near the bottom of the
// list), so nothing around it moves. The expanded card shows the full title and body, the
// actor, the task key and the full time; a digest shows one section per project with its
// stats and activity. Every project name — the row's own project and each digest section
// header — is a link to that project's board.
//
// Whole-row click contract (TPT22): the entire row is one target — click anywhere on a row
// with a task to mark it read, close this modal, and open the task page. A row with no task
// (a digest notification) just marks read and leaves the modal open, since there's nowhere to
// navigate. A project link is the exception: it closes the modal and opens that board, without
// marking the row read or opening the task. Scrolling a row into view inside the list counts
// as "read" too (a seen-observer, modelled on task-detail.js's Notifications-tab
// IntersectionObserver, TPT18) — a long list drains the unread badge as the user scrolls it,
// not only on click.

import { openModal, closeModal, showToast, esc, projectSwatchColor } from '../ui.js';
import { formatDate } from '../utils.js';
import { relativeTime } from '../lib/relative-time.js';
import { fetchNotifications, markNotificationReadOnce } from '../lib/notifications.js';
import { navigate } from '../router.js';

const PAGE_SIZE = 50;
const SEEN_THRESHOLD = 0.5; // a row counts as "viewed" once half of it is in the list's viewport
const SEEN_CLEAR_DELAY_MS = 1500; // (TPT97) how long a scrolled-into-view row holds its unread
  // look before fading to read — long enough to notice, short enough that a user working down
  // the list watches rows drain instead of the whole list flattening instantly on open
const EXPAND_DELAY_MS = 160; // (TPT9188) hover dwell before a row expands — a pointer passing
  // over the list doesn't flash open every row it crosses
const COLLAPSE_DELAY_MS = 100; // grace after the pointer leaves a row — absorbs a brief blip
  // off its edge
const EXPAND_MARGIN = 8; // px an expanded card keeps clear of the list's visible edges

function escapeAttr(s) {
  return esc(s).replace(/"/g, '&quot;');
}

// Digest body lines, as api/src/lib/notification-digest.js writes them (the email's
// parseDigestBody() in api/src/lib/notification-email.js reads the same format): a workspace
// digest opens each project's section with `## #<projectId> <Project name>`, followed by the
// `Daily stats: …` / `Current totals: …` summary lines and `- KEY created: title` /
// `- KEY → status` activity lines.
const DIGEST_SECTION_LINE = /^## #([1-9]\d*)(?: (.*))?$/;
const DIGEST_STATS_LINE = /^(Daily stats|Current totals): (.+)$/;
const DIGEST_STAT_ITEM = /^(\d+) (.+)$/;
const DIGEST_CREATED_LINE = /^-\s+(\S+)\s+created:\s*(.*)$/;
const DIGEST_STATUS_LINE = /^-\s+(\S+)\s+→\s*(.+)$/;

export function isDigestRow(n) {
  return n.type === 'digest' || n.event_type === 'daily_digest';
}

// parseDigestSections(body) → [{ projectId, projectName, stats, activity }]
//   stats:    [{ label: 'Daily stats', items: [{ value: '3', label: 'created' }, …] }, …]
//   activity: [{ key: 'TPT12', text: 'created: Some title' }, …]
// A per-project digest from before TPT9186 has no section header: its lines land in one
// section with projectId null (the row's own project already names it). A line this does not
// recognise is kept as plain activity text (key null) rather than dropped, so a format change
// degrades the display instead of hiding an update.
export function parseDigestSections(body) {
  const sections = [];
  let current = null;
  const open = (projectId, projectName) => {
    current = { projectId, projectName, stats: [], activity: [] };
    sections.push(current);
    return current;
  };
  for (const rawLine of String(body ?? '').split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const header = DIGEST_SECTION_LINE.exec(line);
    if (header) {
      open(Number(header[1]), (header[2] || '').trim());
      continue;
    }
    const section = current || open(null, '');
    const stats = DIGEST_STATS_LINE.exec(line);
    if (stats) {
      section.stats.push({
        label: stats[1],
        items: stats[2].split('·').map((part) => {
          const item = DIGEST_STAT_ITEM.exec(part.trim());
          return item ? { value: item[1], label: item[2] } : { value: '', label: part.trim() };
        }),
      });
      continue;
    }
    let m = DIGEST_CREATED_LINE.exec(line);
    if (m) {
      section.activity.push({ key: m[1], text: `created: ${m[2].trim()}` });
      continue;
    }
    m = DIGEST_STATUS_LINE.exec(line);
    if (m) {
      section.activity.push({ key: m[1], text: `→ ${m[2].trim()}` });
      continue;
    }
    section.activity.push({ key: null, text: line });
  }
  return sections;
}

// A digest row's second line: its projects ("Alpha · Beta"), or for a pre-TPT9186 per-project
// digest, whose row already names its project, its first stats line.
export function digestPreview(sections) {
  const names = sections.filter(s => s.projectId != null).map(s => s.projectName || `Project #${s.projectId}`);
  if (names.length) return names.join(' · ');
  const stats = sections.flatMap(s => s.stats)[0];
  return stats ? stats.items.map(i => (i.value ? `${i.value} ${i.label}` : i.label)).join(' · ') : '';
}

// A project name as a link to that project's board (TPT9188). The click listener in
// openNotificationsModal() owns what a click on it does; the href is the single source of the
// target. No color_scheme on a notification row (GET /workspace/notifications doesn't join it)
// — projectSwatchColor() falls back to its per-project-id hash color the same way
// workspace-tasks.js's rows do before that page's own project list has loaded.
function renderProjectLink(projectId, name) {
  const label = name || `Project #${projectId}`;
  return `<a class="ws-project-badge notif-project-link" href="#/projects/${Number(projectId)}/board" style="--proj-color:${projectSwatchColor(undefined, projectId)}" draggable="false" title="Open the ${escapeAttr(label)} board">${esc(label)}</a>`;
}

function renderDigestSection(section) {
  const updates = section.activity.filter(a => a.key).length;
  const head = section.projectId != null
    ? `<div class="notif-digest-head">${renderProjectLink(section.projectId, section.projectName)}<span class="notif-digest-count">${updates} update${updates === 1 ? '' : 's'}</span></div>`
    : '';
  const stats = section.stats.map(group => `<div class="notif-digest-stats"><span class="notif-digest-stats-label">${esc(group.label)}</span>${group.items.map(item => `<span class="notif-stat">${item.value ? `<b>${esc(item.value)}</b> ` : ''}${esc(item.label)}</span>`).join('')}</div>`).join('');
  const activity = section.activity.length
    ? `<ul class="notif-digest-activity">${section.activity.map(a => `<li>${a.key ? `<span class="notif-digest-key">${esc(a.key)}</span> ` : ''}${esc(a.text)}</li>`).join('')}</ul>`
    : '';
  return `<section class="notif-digest-section">${head}${stats}${activity}</section>`;
}

// What an expanded row adds below its summary: a digest's project sections, then a meta line
// with the task key, the actor and the full time. Hidden by CSS until the row expands.
function renderDetails(n, sections) {
  const content = sections ? `<div class="notif-digest">${sections.map(renderDigestSection).join('')}</div>` : '';
  const meta = [];
  if (n.task_key && n.project_id != null) {
    meta.push(`<a class="task-key-link" href="#/projects/${n.project_id}/tasks/${encodeURIComponent(n.task_key)}" draggable="false" title="Open task page">${esc(n.task_key)}</a>`);
  }
  const actor = n.actor && (n.actor.name || n.actor.email);
  if (actor) meta.push(`<span class="notif-actor">by ${esc(actor)}</span>`);
  if (n.created_at) meta.push(`<time class="notif-time" datetime="${escapeAttr(n.created_at)}">${esc(formatDate(n.created_at))}</time>`);
  return `<div class="notif-details">${content}<div class="notif-meta">${meta.join('<span aria-hidden="true">·</span>')}</div></div>`;
}

function renderNotifRow(n) {
  const unread = !n.read_at;
  const workspaceRow = n.project_id == null;
  const sections = isDigestRow(n) ? parseDigestSections(n.body) : null;
  const project = workspaceRow
    ? '<span class="ws-project-badge notif-workspace-badge" title="Covers all of your projects">All projects</span>'
    : renderProjectLink(n.project_id, n.project_name);
  const when = n.created_at
    ? `<time class="notif-when" datetime="${escapeAttr(n.created_at)}" title="${escapeAttr(formatDate(n.created_at))}">${esc(relativeTime(n.created_at))}</time>`
    : '';
  // The second line. Any row but a digest shows its body itself: CSS folds it onto one ellipsised
  // line until the row expands, then un-clamps it in place as the full body. A digest shows its
  // project names, replaced by its sections once expanded.
  const preview = sections
    ? `<div class="notif-preview">${esc(digestPreview(sections))}</div>`
    : `<div class="notif-preview notif-body">${esc(n.body)}</div>`;
  // A workspace row's data-project-id is empty: markNotificationRead() sends it to the
  // workspace mark-read route instead of a project one (TPT9188).
  return `<li class="notif-row${unread ? ' unread' : ''}" tabindex="0" data-id="${n.id}" data-project-id="${workspaceRow ? '' : n.project_id}">
    <div class="notif-card">
      <div class="notif-summary">
        <span class="notif-dot" ${unread ? '' : 'hidden'} title="Unread"></span>
        <div class="notif-main">
          <div class="notif-title">${esc(n.title)}</div>
          ${preview}
        </div>
        <div class="notif-aside">${when}${project}</div>
      </div>
      ${renderDetails(n, sections)}
    </div>
  </li>`;
}

// renderNotificationsList(rows) — the list markup used by openNotificationsModal()'s initial
// render and its subsequent Load More appends.
export function renderNotificationsList(rows) {
  if (!rows.length) {
    return '<div class="empty text-muted">No notifications</div>';
  }
  return `<ul class="notif-list" id="notif-items" aria-label="Notifications">${rows.map(renderNotifRow).join('')}</ul>`;
}

// Returns the modal's owner token (TPT647). Opened from the navbar, which no page teardown owns,
// so it closes itself on navigation (closeOnNavigate) instead.
export function openNotificationsModal() {
  let rows = [];
  let total = 0;
  let offset = 0;
  let loading = false;
  let seenObserver = null;
  // (TPT97) id -> setTimeout id, for rows the seen-observer PATCHed read but whose unread
  // visual is still being held for SEEN_CLEAR_DELAY_MS. Declared here (not inside onMount) so
  // onClose can also reach it. Cleared on modal close and on a click that clears the same
  // row's visual early.
  const pendingClears = new Map();
  // (TPT9188) the hover dwell timer, each left row's pending collapse, and the queued refit of
  // an expanded row after a list scroll — all cancelled on close.
  let hoverTimer = null;
  const collapseTimers = new Map();
  let refitFrame = 0;

  const body = `
    <div id="notif-list" class="notif-scroll"><div class="empty"><span class="spinner"></span></div></div>
    <div id="notif-pagination"></div>`;

  return openModal('Notifications', body, {
    closeOnNavigate: true,
    onMount(overlay) {
      const modalEl = overlay.querySelector('.modal');
      // modal-wide: room for a row's title, preview and time/project column (TPT22).
      // notif-modal: the list, not the whole modal, is the scroll box an expanded row is fitted
      // into (TPT9188).
      if (modalEl) modalEl.classList.add('modal-wide', 'notif-modal');
      const listEl = overlay.querySelector('#notif-list');
      const paginationEl = overlay.querySelector('#notif-pagination');

      function renderPagination() {
        if (!rows.length) { paginationEl.innerHTML = ''; return; }
        paginationEl.innerHTML = `<div class="ws-pagination">
          <span class="text-muted">Showing ${rows.length} of ${total} notifications</span>
          ${rows.length < total ? '<button class="btn btn-ghost btn-sm" id="notif-load-more">Load More</button>' : ''}
        </div>`;
        const btn = paginationEl.querySelector('#notif-load-more');
        if (btn) btn.addEventListener('click', () => { offset += PAGE_SIZE; load(true); });
      }

      // clearUnreadVisual(row, localRow) — drops the .unread class + hides the dot, i.e. the
      // part of markRowRead that used to run synchronously. Guarded on row.isConnected: a Load
      // More append replaces #notif-items wholesale, so a deferred clear (see delay below) can
      // fire after its row is long gone from the DOM.
      function clearUnreadVisual(row, localRow) {
        if (localRow) localRow.read_at = new Date().toISOString();
        if (!row.isConnected) return;
        row.classList.remove('unread');
        const dot = row.querySelector('.notif-dot');
        if (dot) dot.hidden = true;
      }

      // markRowRead(row, id, projectId, localRow, opts) — shared by the click handler and the
      // seen-observer below. Fires the PATCH un-awaited (a caller navigating away must never
      // wait on it) so the bell badge always drains immediately. `projectId` is the row's
      // data-project-id: empty for a workspace row (TPT9186), which markNotificationRead()
      // routes to the workspace mark-read endpoint. `delay` controls when the *visual* (the
      // .unread class/tint/dot) actually clears (TPT97):
      //   - 0 (row click): clear at once — today's behavior, the row is about to disappear
      //     under a navigation anyway.
      //   - >0 (seen-observer): hold the unread look for `delay` ms so a user scrolling through
      //     the list watches rows fade to read one at a time instead of the whole list
      //     flattening the instant the modal opens. The pending timer is tracked in
      //     `pendingClears` so onClose() can cancel it and a same-row click can jump the queue.
      // On failure, restores the DOM only if the row is still attached (it won't be after we've
      // navigated away) and always reverts the localRow's read_at so a later Load More
      // re-render doesn't repaint it as read. markNotificationReadOnce() (lib/notifications.js)
      // dedupes the click and the observer racing for the same row.
      function markRowRead(row, id, projectId, localRow, { delay = 0 } = {}) {
        if (!row.classList.contains('unread')) return;
        const key = String(id);
        const pending = pendingClears.get(key);
        if (pending != null) { clearTimeout(pending); pendingClears.delete(key); }

        if (delay > 0) {
          pendingClears.set(key, setTimeout(() => {
            pendingClears.delete(key);
            clearUnreadVisual(row, localRow);
          }, delay));
        } else {
          clearUnreadVisual(row, localRow);
        }

        markNotificationReadOnce(projectId, id).catch((err) => {
          showToast(err.message || 'Failed to mark read', 'error');
          const stillPending = pendingClears.get(key);
          if (stillPending != null) { clearTimeout(stillPending); pendingClears.delete(key); }
          if (row.isConnected) {
            row.classList.add('unread');
            const dot = row.querySelector('.notif-dot');
            if (dot) dot.hidden = false;
          }
          if (localRow) localRow.read_at = null;
        });
      }

      function disconnectSeenObserver() {
        if (seenObserver) { seenObserver.disconnect(); seenObserver = null; }
      }

      // observeUnreadRows() — (TPT22) re-armed after every render (initial load and each Load
      // More append both replace #notif-items, discarding any previously observed nodes).
      // Watches only the currently-unread rows; a row is unobserved the moment it's marked
      // read, whether that happens here or via a row click that beat the scroll to it. The
      // root is the list, the modal's scroll box (TPT9188).
      function observeUnreadRows() {
        disconnectSeenObserver();
        const unreadRows = listEl.querySelectorAll('.notif-row.unread');
        if (!unreadRows.length) return;
        seenObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const row = entry.target;
            seenObserver.unobserve(row);
            if (!row.classList.contains('unread')) continue;
            markRowRead(row, row.dataset.id, row.dataset.projectId, rows.find(r => String(r.id) === row.dataset.id), { delay: SEEN_CLEAR_DELAY_MS });
          }
        }, { root: listEl, threshold: SEEN_THRESHOLD });
        unreadRows.forEach(row => seenObserver.observe(row));
      }

      async function load(append) {
        if (loading) return;
        loading = true;
        if (!append) {
          offset = 0;
          listEl.innerHTML = '<div class="empty"><span class="spinner"></span></div>';
        }
        try {
          const res = await fetchNotifications({ limit: PAGE_SIZE, offset });
          rows = append ? [...rows, ...res.notifications] : res.notifications;
          total = res.total;
          listEl.innerHTML = renderNotificationsList(rows);
          observeUnreadRows();
        } catch (err) {
          listEl.innerHTML = '<div class="empty text-muted">Failed to load notifications</div>';
          showToast(err.message || 'Failed to load notifications', 'error');
        }
        loading = false;
        renderPagination();
      }

      // ── Hover / keyboard-focus expand (TPT9188) ──

      // Keyboard focus — a Tab onto a row or a link inside it — expands the row. A mouse press
      // also focuses the row (tabindex="0") but must not pin it open: hover owns the pointer.
      function keyboardFocused(el) {
        try { return el.matches(':focus-visible'); } catch (_) { return true; }
      }

      // The row holding keyboard focus, or null.
      function focusedRow() {
        const el = document.activeElement;
        const row = el && el.closest ? el.closest('.notif-row') : null;
        return row && listEl.contains(row) && keyboardFocused(el) ? row : null;
      }

      // expandRow(row) — pins the row's <li> at its current height first, so the rows around it
      // never move, then turns its card into an overlay (styles.css .notif-row.expanded) that
      // shows the details and grows down over the rows below — or up from the row's bottom edge
      // (.expand-up) when it doesn't fit below and there is more room above, as for a row near
      // the bottom of the visible list. A card taller than its side's room is capped to it and
      // scrolls inside itself. One row is expanded at a time.
      function expandRow(row) {
        if (!row.isConnected) return;
        listEl.querySelectorAll('.notif-row.expanded').forEach((other) => {
          if (other !== row) collapseRow(other);
        });
        if (row.classList.contains('expanded')) return;
        const card = row.querySelector('.notif-card');
        const rowRect = row.getBoundingClientRect();
        row.style.height = rowRect.height + 'px';
        row.classList.add('expanded');
        const box = listEl.getBoundingClientRect();
        const below = box.bottom - rowRect.top - EXPAND_MARGIN;
        const above = rowRect.bottom - box.top - EXPAND_MARGIN;
        const height = card.scrollHeight;
        const up = height > below && above > below;
        row.classList.toggle('expand-up', up);
        const room = Math.max(up ? above : below, rowRect.height);
        if (height > room) {
          card.style.maxHeight = room + 'px';
          card.style.overflowY = 'auto';
        }
      }

      function collapseRow(row) {
        if (!row.classList.contains('expanded')) return;
        row.classList.remove('expanded', 'expand-up');
        row.style.height = '';
        const card = row.querySelector('.notif-card');
        card.style.maxHeight = '';
        card.style.overflowY = '';
        card.scrollTop = 0;
      }

      // Pointer: capture-phase mouseenter/mouseleave delegated from the list (neither bubbles),
      // taken only for the row element itself. An expanded card is the row's own descendant, so
      // the pointer moving over the part of it that covers other rows is still inside this row.
      listEl.addEventListener('mouseenter', (e) => {
        const row = e.target;
        if (!row.classList || !row.classList.contains('notif-row')) return;
        clearTimeout(collapseTimers.get(row));
        collapseTimers.delete(row);
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          hoverTimer = null;
          if (row.isConnected && row.matches(':hover')) expandRow(row);
        }, EXPAND_DELAY_MS);
      }, true);

      listEl.addEventListener('mouseleave', (e) => {
        const row = e.target;
        if (!row.classList || !row.classList.contains('notif-row')) return;
        clearTimeout(hoverTimer);
        hoverTimer = null;
        clearTimeout(collapseTimers.get(row));
        collapseTimers.set(row, setTimeout(() => {
          collapseTimers.delete(row);
          if (!row.isConnected || row.matches(':hover') || focusedRow() === row) return;
          collapseRow(row);
          // A keyboard-focused row that this hover took over from gets its expansion back.
          const focused = focusedRow();
          if (focused) expandRow(focused);
        }, COLLAPSE_DELAY_MS));
      }, true);

      listEl.addEventListener('focusin', (e) => {
        const row = e.target.closest('.notif-row');
        if (row && keyboardFocused(e.target)) expandRow(row);
      });

      listEl.addEventListener('focusout', (e) => {
        const row = e.target.closest('.notif-row');
        if (!row || row.contains(e.relatedTarget)) return;
        if (!row.matches(':hover')) collapseRow(row);
      });

      // An expanded card moves with its row when the list scrolls (Tab scrolling the next row
      // into view, the wheel): refit it to its new room, once per frame, keeping the card's own
      // scroll position.
      listEl.addEventListener('scroll', () => {
        if (refitFrame) return;
        refitFrame = requestAnimationFrame(() => {
          refitFrame = 0;
          const row = listEl.querySelector('.notif-row.expanded');
          if (!row) return;
          const card = row.querySelector('.notif-card');
          const scrollTop = card.scrollTop;
          collapseRow(row);
          expandRow(row);
          card.scrollTop = scrollTop;
        });
      }, { passive: true });

      // Whole row is one click target (TPT22). A plain click anywhere — including the
      // task-key link — marks the row read, closes this modal, and navigates via the link's
      // own href, so the URL lives in exactly one place (the rendered <a>). A modifier-click
      // on the link itself (cmd/ctrl/shift/alt) is left alone so the browser can open its own
      // new tab; the row still gets marked read either way. A row with no task-key link (a
      // digest notification) just marks read and stays open — nothing to navigate to. On a
      // device without hover, a tap there toggles the row's expansion instead: the only way
      // to read a digest's sections without a pointer or a keyboard.
      function activateRow(row, e) {
        const id = row.dataset.id;
        const local = rows.find(r => String(r.id) === id);
        const link = row.querySelector('.task-key-link');
        const clickedLink = e.target.closest('a');
        const modifierHeld = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

        markRowRead(row, id, row.dataset.projectId, local);

        if (clickedLink && modifierHeld) return; // let the browser open its own new tab
        if (!link) { // digest row — nothing to open, stay put
          if (window.matchMedia && window.matchMedia('(hover: none)').matches) {
            if (row.classList.contains('expanded')) collapseRow(row);
            else expandRow(row);
          }
          return;
        }

        e.preventDefault();
        closeModal();
        navigate(link.getAttribute('href').slice(1));
      }

      listEl.addEventListener('click', (e) => {
        const row = e.target.closest('.notif-row');
        if (!row) return;

        // A project link — the row's own project or a digest section header (TPT9188) — opens
        // that project's board. It never marks the row read or opens the task. A modifier-click
        // is left to the browser's own new tab, with the modal left open.
        const projectLink = e.target.closest('.notif-project-link');
        if (projectLink) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          closeModal();
          navigate(projectLink.getAttribute('href').slice(1));
          return;
        }

        activateRow(row, e);
      });

      // Enter / Space on a focused row act like a click on it. A focused link inside the row
      // gets the browser's own Enter → click, handled by the click listener above.
      listEl.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const row = e.target;
        if (!row.classList || !row.classList.contains('notif-row')) return;
        e.preventDefault();
        activateRow(row, e);
      });

      load(false);
    },
    onClose() {
      // seenObserver is a closure var initialized above onMount's scope; onMount always runs
      // before onClose can fire, so this is never called with it uninitialized.
      if (seenObserver) seenObserver.disconnect();
      // (TPT97) drop any in-flight fade timers so they never fire against a closed modal's
      // detached rows.
      pendingClears.forEach(clearTimeout);
      pendingClears.clear();
      // (TPT9188) same for the expand/collapse timers and a queued refit.
      clearTimeout(hoverTimer);
      collapseTimers.forEach(clearTimeout);
      collapseTimers.clear();
      if (refitFrame) cancelAnimationFrame(refitFrame);
    },
  });
}
